import type { ConceptChip } from '@mathflat/domain/ConceptChip'
import type {
  GroupCommonWeakConceptChipParams,
  PersonalizedWeakConceptChipParams,
  PersonalizedWeakConceptChipWorksheetBody,
  WeakConceptChipGroupTarget,
  WeakConceptChipProblemCount,
} from '@mathflat/domain/Derivation'
import type { WrongWorksheetType } from '@mathflat/domain/MakeWorksheet'
import type { AllGradeSubject } from '@mathflat/domain/SchoolSystem'
import { makeAutoObservable } from 'mobx'

import { typeSafeAssign } from '@/utils/common'

import { SchoolGrade } from '../Student'
import type { MakeWorksheetByChapterOption } from '../Worksheet'

export type 그룹유형분석옵션ParamT = {
  conceptChipIds: ConceptChip['conceptChipId'][]
  conceptChipIdsForGroupCommon: ConceptChip['conceptChipId'][]
  arithmeticOnly: boolean
} & WeakConceptChipGroupTarget & { groupName: string }

// TODO: 오답학습지 리팩토링 삭제
export class 그룹유형분석옵션 {
  conceptChipIds: ConceptChip['conceptChipId'][]
  conceptChipIdsForGroupCommon: ConceptChip['conceptChipId'][]
  arithmeticOnly: boolean
  maxProblemCount: GroupCommonWeakConceptChipParams['maxProblemCount'] = null
  limitProblemCountPerConceptChip: GroupCommonWeakConceptChipParams['limitProblemCountPerConceptChip'] =
    null

  onlyAutoScorable: PersonalizedWeakConceptChipParams['autoScorableOnly'] = true
  excludePrevious: PersonalizedWeakConceptChipParams['excludePrevious'] = false

  excludePracticeProblem: boolean = false
  excludeScoredProblem: boolean = false

  groupKeyAndInfo: WeakConceptChipGroupTarget & { groupName: string }

  // params외 data
  assigningType: 'WEAKNESS' | 'PROBLEM_COUNT' = 'PROBLEM_COUNT'

  // 오답학습지출제옵션 타입
  readonly problemRequestType = 'SAME'
  readonly wrongWorksheetType: WrongWorksheetType = 'WRONG_CONCEPT_WORKSHEET'

  constructor({
    conceptChipIds,
    conceptChipIdsForGroupCommon,
    arithmeticOnly,
    ...groupKeyAndInfo
  }: 그룹유형분석옵션ParamT) {
    makeAutoObservable(this)

    this.conceptChipIds = conceptChipIds
    this.conceptChipIdsForGroupCommon = conceptChipIdsForGroupCommon
    this.arithmeticOnly = arithmeticOnly

    if (groupKeyAndInfo.studentGroupKey === 'GRADE') {
      this.groupKeyAndInfo = {
        studentGroupKey: groupKeyAndInfo.studentGroupKey,
        studentGrade: groupKeyAndInfo.studentGrade,
        studentSchoolType: groupKeyAndInfo.studentSchoolType,
        groupName: groupKeyAndInfo.groupName,
      }
    } else {
      this.groupKeyAndInfo = {
        studentGroupKey: groupKeyAndInfo.studentGroupKey,
        lessonClassId: groupKeyAndInfo.lessonClassId,
        groupName: groupKeyAndInfo.groupName,
      }
    }
  }

  update = (options?: Partial<Omit<그룹유형분석옵션, 'excludePracticeProblem'>>) => {
    if (options) {
      typeSafeAssign(this, options)
    }

    return this
  }

  get maxAndDefaultProblemSize() {
    const conceptChipSize = this.conceptChipIds.length

    if (conceptChipSize <= 10) return { max: 15, defaultSize: 5 }
    if (conceptChipSize <= 15) return { max: 10, defaultSize: 5 }
    if (conceptChipSize <= 25) return { max: 6, defaultSize: 5 }
    if (conceptChipSize <= 30) return { max: 5, defaultSize: 3 }

    return { max: 3, defaultSize: 3 }
  }

  get params(): PersonalizedWeakConceptChipParams {
    if (this.conceptChipIds.length === 0) throw Error('필수값이 없습니다.')

    return {
      conceptChipIds: this.conceptChipIds,
      practiceTestOption: this.excludePracticeProblem ? 'EXCLUDE' : 'INCLUDE',
      autoScorableOnly: this.onlyAutoScorable,
      excludePrevious: this.excludePrevious,
      maxProblemCount: this.maxProblemCount ?? 0,
      arithmeticOnly: this.arithmeticOnly,
      ...this.groupKeyAndInfo,
    }
  }

  get commonGroupParams(): GroupCommonWeakConceptChipParams {
    if (this.conceptChipIdsForGroupCommon.length === 0) throw Error('필수값이 없습니다.')

    const hasLimit =
      this.limitProblemCountPerConceptChip !== null &&
      this.limitProblemCountPerConceptChip !== undefined

    const problemCount: WeakConceptChipProblemCount =
      hasLimit && this.assigningType === 'WEAKNESS'
        ? {
            maxProblemCount: null,
            limitProblemCountPerConceptChip: this.limitProblemCountPerConceptChip as number,
          }
        : {
            maxProblemCount: this.maxProblemCount ?? 0,
            limitProblemCountPerConceptChip: null,
          }

    return {
      conceptChipIds: this.conceptChipIdsForGroupCommon,
      practiceTestOption: this.excludePracticeProblem ? 'EXCLUDE' : 'INCLUDE',
      autoScorableOnly: this.onlyAutoScorable,
      excludePrevious: this.excludePrevious,
      arithmeticOnly: this.arithmeticOnly,
      ...this.groupKeyAndInfo,
      ...problemCount,
    }
  }

  get makeCommonGroupWorksheetParams(): Partial<MakeWorksheetByChapterOption> {
    if (this.conceptChipIds.length === 0) throw Error('필수값이 없습니다.')

    return {
      practiceTest: this.excludePracticeProblem ? 'EXCLUDE' : 'INCLUDE',
      onlyAutoScorable: this.onlyAutoScorable,
      excludePrevious: this.excludePrevious,
      arithmeticOnly: this.arithmeticOnly,
    }
  }

  get postPersonlizedWeakWorksheetParams(): Pick<
    PersonalizedWeakConceptChipWorksheetBody,
    | 'groupKey'
    | 'groupName'
    | 'groupType'
    | 'autoScorableOnly'
    | 'practiceTestOption'
    | 'arithmeticOnly'
  > {
    if (this.groupKeyAndInfo.studentGroupKey === 'GRADE') {
      const { studentGrade, studentSchoolType } = this.groupKeyAndInfo
      const koSchoolGrade = SchoolGrade.getKoreanSchoolGrade(
        studentGrade.toString() as AllGradeSubject,
        studentSchoolType,
      )

      return {
        practiceTestOption: this.excludePracticeProblem ? 'EXCLUDE' : 'INCLUDE',
        autoScorableOnly: this.onlyAutoScorable,
        groupKey: koSchoolGrade,
        groupName: this.groupKeyAndInfo.groupName,
        groupType: 'grade',
        arithmeticOnly: this.arithmeticOnly,
      }
    }

    return {
      practiceTestOption: this.excludePracticeProblem ? 'EXCLUDE' : 'INCLUDE',
      autoScorableOnly: this.onlyAutoScorable,
      groupKey: String(this.groupKeyAndInfo.lessonClassId),
      groupName: this.groupKeyAndInfo.groupName,
      groupType: 'lesson-class',
      arithmeticOnly: this.arithmeticOnly,
    }
  }
}
